import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import { 
  FaArrowLeft, 
  FaBuilding, 
  FaEnvelope, 
  FaPhone, 
  FaCalendar, 
  FaUsers, 
  FaUser, 
  FaSearch,
  FaEye,
  FaEdit
} from 'react-icons/fa';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 8px 0;
  
  &:hover {
    color: #5a6fd8;
  }
`;

const ClientInfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
`;

const ClientTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
  font-weight: 600;
`;

const ClientStatus = styled.span`
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const ClientDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 25px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  
  svg {
    color: #667eea;
    font-size: 16px;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #6c757d;
  font-size: 14px;
`;

const DetailValue = styled.span`
  color: #2c3e50;
  font-weight: 500;
`;

const UsersSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const UsersHeader = styled.div`
  padding: 25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`;

const UsersTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 15px 10px 40px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const UsersTable = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e9ecef;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 15px;
  color: #2c3e50;
`;

const UserStatus = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f0f4ff;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        const [clientData, usersData] = await Promise.all([
          clientService.getClientById(id),
          clientService.getClientUsers(id)
        ]);
        setClient(clientData);
        // Garante que users seja sempre um array
        setUsers(Array.isArray(usersData) ? usersData : []);
      } catch (err) {
        setError('Erro ao carregar dados do cliente');
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [id]);

  // Se não há dados da API, não exibe nada
  if (!client) {
    return (
      <Container>
        <BackButton onClick={() => navigate('/clientes')}>
          <FaArrowLeft />
          Voltar para Clientes
        </BackButton>
        <ErrorMessage>Cliente não encontrado</ErrorMessage>
      </Container>
    );
  }

  const filteredUsers = Array.isArray(users) ? users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <Container>
      <BackButton onClick={() => navigate('/clientes')}>
        <FaArrowLeft />
        Voltar para Clientes
      </BackButton>

      <ClientInfoCard>
        <ClientHeader>
          <ClientTitle>{client.nome_fantasia || client.name}</ClientTitle>
          <ClientStatus $active={client.status === 'active'}>
            {client.status === 'active' ? 'Ativo' : 'Inativo'}
          </ClientStatus>
        </ClientHeader>

        <ClientDetails>
          <DetailItem>
            <FaEnvelope />
            <div>
              <DetailLabel>Email:</DetailLabel>
              <DetailValue>{client.email}</DetailValue>
            </div>
          </DetailItem>

          <DetailItem>
            <FaPhone />
            <div>
              <DetailLabel>Telefone:</DetailLabel>
              <DetailValue>{client.telefone || client.phone}</DetailValue>
            </div>
          </DetailItem>

          <DetailItem>
            <FaCalendar />
            <div>
              <DetailLabel>Cadastrado em:</DetailLabel>
              <DetailValue>{client.created_at || client.createdAt}</DetailValue>
            </div>
          </DetailItem>

          <DetailItem>
            <FaBuilding />
            <div>
              <DetailLabel>CNPJ:</DetailLabel>
              <DetailValue>{client.cnpj}</DetailValue>
            </div>
          </DetailItem>
        </ClientDetails>
      </ClientInfoCard>

      <UsersSection>
        <UsersHeader>
          <UsersTitle>
            <FaUsers style={{ marginRight: '10px' }} />
            Usuários ({filteredUsers.length})
          </UsersTitle>
          <SearchContainer>
            <SearchIcon>
              <FaSearch />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Buscar usuários..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
        </UsersHeader>

        <UsersTable>
          {filteredUsers.length === 0 ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#6c757d',
              background: '#f8f9fa'
            }}>
              <FaUsers style={{ fontSize: '48px', marginBottom: '15px', color: '#dee2e6' }} />
              <p>Nenhum usuário encontrado para este cliente</p>
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <TableHeader>Nome</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Cargo</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Último Login</TableHeader>
                  <TableHeader>Ações</TableHeader>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaUser style={{ color: '#667eea' }} />
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>
                                          <UserStatus $active={user.status === 'active'}>
                      {user.status === 'active' ? 'Ativo' : 'Inativo'}
                    </UserStatus>
                    </TableCell>
                    <TableCell>{user.lastLogin || user.last_login || 'N/A'}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <ActionButton title="Ver detalhes">
                          <FaEye />
                        </ActionButton>
                        <ActionButton title="Editar">
                          <FaEdit />
                        </ActionButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </UsersTable>
      </UsersSection>
    </Container>
  );
};

export default ClientDetail; 